import { z } from 'zod';
import type { IntentCandidate, LocalAiRuntime, VoiceManifest } from '../types/LocalAiTypes';
import { recordLocalAiDuration } from '../models/ModelTelemetry';
import { workerModelConfig } from '../models/WorkerModelConfig';
import { WorkerClient } from '../workers/WorkerClient';

const responseSchema = z.object({
  intent: z.string().min(1).max(120),
  confidence: z.number().min(0).max(1),
  parameters: z.record(z.string(), z.union([z.string(), z.number(), z.boolean()])).default({}),
});

export class LocalLlmParser {
  private readonly client: WorkerClient;
  private initialization?: Promise<void>;

  public constructor(
    private readonly manifest: VoiceManifest,
    private readonly runtime: LocalAiRuntime,
    private readonly halfPrecision: boolean,
  ) {
    this.client = new WorkerClient(new Worker(new URL('../workers/localLlm.worker.ts', import.meta.url), { type: 'module' }));
  }

  public async parse(transcript: string, candidates: readonly IntentCandidate[]): Promise<IntentCandidate | undefined> {
    const started = performance.now();
    try {
      await this.initialize();
      const allowed = candidates.slice(0, 5).map((candidate) => ({
        id: candidate.intent.id,
        parameters: candidate.intent.parameters.map((parameter) => parameter.name),
      }));
      const prompt = [
        'Select exactly one Varza intent from the allowlist. Never invent an intent or parameter.',
        'Return only compact JSON: {"intent":"...","confidence":0.0,"parameters":{}}.',
        `Allowlist: ${JSON.stringify(allowed)}`,
        `Transcript: ${JSON.stringify(transcript)}`,
      ].join('\n');
      const raw = await this.client.request<unknown>({ type: 'PARSE', payload: prompt }, { timeoutMs: 180_000 });
      const text = typeof raw === 'string' ? raw : JSON.stringify(raw);
      const object = /\{[\s\S]*\}/u.exec(text)?.[0];
      if (!object) return undefined;
      const parsed = responseSchema.safeParse(JSON.parse(object));
      if (!parsed.success) return undefined;
      const base = candidates.find((candidate) => candidate.intent.id === parsed.data.intent);
      if (!base || parsed.data.confidence < 0.7) return undefined;
      const allowedParameters = new Set(base.intent.parameters.map((parameter) => parameter.name));
      return {
        ...base,
        source: 'local-llm',
        confidence: Math.min(0.89, parsed.data.confidence),
        parameters: Object.fromEntries(Object.entries(parsed.data.parameters).filter(([name]) => allowedParameters.has(name))),
      };
    } catch {
      return undefined;
    } finally {
      recordLocalAiDuration('llm', performance.now() - started);
    }
  }

  public dispose(): void {
    this.client.dispose();
  }

  private initialize(): Promise<void> {
    if (!this.initialization) {
      this.initialization = this.client.request<void>({
        type: 'INIT',
        config: workerModelConfig(this.manifest, 'intent.llm', this.runtime, this.halfPrecision),
      }, { timeoutMs: 300_000 });
    }
    return this.initialization;
  }
}
